import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/seo/SEOHead';
import { ArrowRight, Building2, FileCheck, Clock, CheckCircle, AlertCircle, Shield, Users } from 'lucide-react';

const requirements = [
  {
    title: "RCMP Criminal Record Check",
    description: "Certified criminal record check from the Royal Canadian Mounted Police.",
    timeline: "2-4 weeks",
  },
  {
    title: "Global Affairs Canada Authentication",
    description: "Document authentication through Global Affairs Canada for international use.",
    timeline: "1-2 weeks",
  },
  {
    title: "UAE Embassy Attestation",
    description: "Final attestation by the UAE Embassy in Ottawa or Consulate.",
    timeline: "1-2 weeks",
  },
  {
    title: "Document Translation",
    description: "Certified Arabic translation if required by the receiving entity.",
    timeline: "3-5 business days",
  },
];

const commonUses = [
  "Employment visa applications",
  "Residency permit processing",
  "Professional license registration",
  "Business establishment documentation",
  "Family sponsorship applications",
  "Educational institution enrollment",
];

const processSteps = [
  { number: 1, title: "Initial Consultation", description: "Review your specific requirements and destination emirate needs." },
  { number: 2, title: "Document Collection", description: "Coordinate RCMP fingerprinting and criminal record check request." },
  { number: 3, title: "Authentication Process", description: "Submit documents through Global Affairs Canada authentication." },
  { number: 4, title: "Embassy Attestation", description: "Final attestation through UAE diplomatic channels." },
  { number: 5, title: "Document Delivery", description: "Complete documentation package delivered ready for UAE submission." },
];

export default function Dubai() {
  return (
    <>
      <SEOHead 
        page="internationalFilings"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'International Filings', url: '/international-filings' },
        ]}
      />
      
      <div data-testid="dubai-page">
        {/* Hero Section */}
        <section className="bg-[hsl(215,50%,23%)] text-white py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
            <p className="text-white/70 text-sm uppercase tracking-wide mb-2">United Arab Emirates</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">Dubai Document Services</h1>
            <p className="text-white/80 text-lg mb-6">
              Comprehensive document preparation and authentication services for 
              individuals relocating to or conducting business in Dubai and the 
              United Arab Emirates.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[hsl(215,50%,23%)] hover:bg-gray-100 font-semibold px-6 py-3 rounded-sm" data-testid="dubai-cta-btn">
                Contact for Inquiries
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="gov-section bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="gov-section-title">UAE Document Requirements</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  The United Arab Emirates requires authenticated police clearance 
                  certificates and criminal record checks for various visa, residency, 
                  and professional license applications.
                </p>
                <p>
                  Documents originating in Canada must undergo a specific authentication 
                  process before being accepted by UAE authorities. This multi-step 
                  process ensures documents are recognized as legitimate by the UAE government.
                </p>
              </div>
            </div>
            
            <div className="gov-panel">
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-4 text-lg">Common Uses</h3>
              <div className="space-y-3">
                {commonUses.map((use, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{use}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="gov-section-alt">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">Required Documentation</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {requirements.map((req, index) => (
              <div 
                key={index}
                className="gov-panel"
                data-testid={`dubai-req-${index + 1}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <FileCheck className="h-6 w-6 text-[hsl(215,50%,23%)]" />
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-sm text-xs text-gray-600">
                    <Clock className="h-3 w-3 mr-1" />
                    {req.timeline}
                  </span>
                </div>
                <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">{req.title}</h3>
                <p className="text-gray-600 text-sm">{req.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="gov-section bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">How We Help</h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="gov-panel flex items-start gap-4"
                data-testid={`dubai-step-${index + 1}`}
              >
                <div className="w-10 h-10 rounded-full bg-[hsl(215,50%,23%)] text-white flex items-center justify-center font-bold flex-shrink-0">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-bold text-[hsl(215,50%,23%)] mb-1">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="gov-section-alt">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="gov-panel border-2 border-[hsl(215,50%,23%)]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-[hsl(215,50%,23%)] mb-2">4-8</div>
                <div className="text-gray-600">Weeks Total Timeline</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[hsl(215,50%,23%)] mb-2">5</div>
                <div className="text-gray-600">Step Process</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[hsl(215,50%,23%)] mb-2">100%</div>
                <div className="text-gray-600">Document Coordination</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-800 mb-2">Important Notice</h3>
                <p className="text-amber-700 text-sm">
                  Canadian Document Services Inc. provides administrative document preparation 
                  and coordination services only. We are not affiliated with the UAE government 
                  or any UAE government agency. Requirements may change without notice. 
                  Always verify current requirements with your receiving entity in the UAE.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gov-section-alt">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Building2 className="h-12 w-12 text-[hsl(215,50%,23%)] mx-auto mb-6" />
          <h2 className="gov-section-title">Relocating to Dubai?</h2>
          <p className="gov-subtitle mb-8">
            Contact us for a consultation on your specific documentation requirements.
          </p>
          <Link to="/contact">
            <Button className="gov-btn-primary" data-testid="dubai-bottom-cta-btn">
              Contact Us for Dubai Filings
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Internal Links */}
      <section className="bg-white py-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-lg font-bold text-[hsl(215,50%,23%)] mb-4">Related Services</h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/record-suspensions" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              Record Suspension →
            </Link>
            <Link to="/us-entry-waivers" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              U.S. Entry Waivers →
            </Link>
            <Link to="/contact" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              Contact Us →
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
